import {
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useBoolean,
} from '@chakra-ui/react';
import { DocusealForm } from '@docuseal/react';
import { ReactComponent as CrownIcon } from 'assets/icons/crown_upgrade_fill.svg';
import interactiveSignatureGatedHoverImg from 'assets/images/interactionTypes/interactive_signature_gated_hover.svg?url';
import interactiveSignatureHoverImg from 'assets/images/interactionTypes/interactive_signature_hover.svg?url';
import { DocuSealSubmitDocumentResponse, cloneDocuSealDocument } from 'libs/e2api/docuseal';
import {
  CLICK_ON_PAID_FEATURE,
  Mixpanel,
  SIGNATURE_EDITED,
  SIGNATURE_MODAL_OPENED,
  UPGRADE,
} from 'libs/third-party/Mixpanel';
import { store } from 'modules/Editor/store';
import { setIsGatedSignatureModalOpen } from 'modules/Editor/store/editorSettingsSlice';
import { useUser } from 'modules/User';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';
import { ReadOnlyWidgetBase, TransparentImg, WidgetBase, WidgetToolbar, useEditor, useWidget } from 'widgets/sdk';

import { SignatureWidgetData } from './SignatureWidget.types';
import { SignatureWidgetToolbarMenu } from './SignatureWidgetToolbar';

interface ModalProps {
  isOpen: boolean;
  allowToResubmit?: boolean;
  onConfirm: (data: any) => void;
  onClose: () => void;
}

interface HoverElementProps {
  isGated: boolean;
  borderRadius: number;
}

export const SignatureWidget = () => {
  const { slug, updateWidget } = useWidget<SignatureWidgetData>();
  const [isModalOpen, setIsModalOpen] = useBoolean(false);
  // control the resubmission of the document
  const [canResubmit, setCanResubmit] = useBoolean(true);

  const token = useLoaderData() as string;
  const { isFreeUser, isPremiumUser, isEducationUser } = useUser();
  const { enableEditorKeyboardShortcuts, disableEditorKeyboardShortcuts } = useEditor();

  const isGated = isFreeUser || isPremiumUser || isEducationUser;

  const onDocumentSubmit = useCallback((data: DocuSealSubmitDocumentResponse) => {
    // get signature image URL from docuseal response
    const signatureImageUrl = data.values[0].value;

    updateWidget({ submissionId: data.submission_id, submission: data, imageURL: signatureImageUrl });

    // limit the user to resubmit the document after first submission
    setCanResubmit.off();

    // MP event
    Mixpanel.track(SIGNATURE_EDITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = useCallback(() => {
    setIsModalOpen.on();
    // disable keyboard shortcuts when the modal is open
    disableEditorKeyboardShortcuts();
    // MP event
    Mixpanel.track(SIGNATURE_MODAL_OPENED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen.off();
    // enable keyboard shortcuts again
    enableEditorKeyboardShortcuts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // at first, we will only create docuseal document when user opens the modal
  const handleOpen = useCallback(async () => {
    if (isGated) {
      // open upgrade modal
      store.dispatch(setIsGatedSignatureModalOpen(true));
      return;
    }

    openModal();

    if (!slug) {
      const { slug } = await cloneDocuSealDocument(token);

      // update widget with docuseal slug to render it on modal
      updateWidget({ slug });
    }
  }, [isGated, openModal, slug, token, updateWidget]);

  return (
    <WidgetBase>
      <WidgetToolbar>
        <SignatureWidgetToolbarMenu onClick={handleOpen} />
      </WidgetToolbar>
      <InteractiveSignature onDoubleClick={handleOpen} />
      <SignatureModal
        isOpen={isModalOpen}
        allowToResubmit={canResubmit}
        onClose={closeModal}
        onConfirm={onDocumentSubmit}
      />
    </WidgetBase>
  );
};

const signaturePadStyle: string = `
  /* hide unused elements */
  .bg-yellow-100,
  .field-area,
  #minimize_form_button {
    display: none;
  }
  /* manage style of the form container */
  #form_container,
  #scrollbox {
    height: 400px;
    box-shadow: none;
    background-color: white;
    border: none;
    padding: 0;
  }
  #scrollbox .shadow-md {
    box-shadow: none;
    --tw-shadow: 0;
    --tw-shadow-colored: 0;
  }
  /* hide steps buttons */
  #form_container a:empty {
    display: none;
  }
  /* TODO - customize buttons */
  #submit_form_button,
  .base-button,
  .base-button:hover {
    background-color: var(--vg-colors-green-500);
    color: white;
    padding: 8px 16px;
    border-radius: 4px;
    border: none;
    cursor: pointer;
  }
  .base-button:disabled {
    cursor: not-allowed !important;
  }
`;

const InteractiveSignature = ({ onDoubleClick, isReadonly }: { onDoubleClick: () => void; isReadonly?: boolean }) => {
  const { backgroundColor, borderRadius, borderColor, borderWidth, imageURL, submission } =
    useWidget<SignatureWidgetData>();
  const [isHover, setIsHover] = useBoolean(false);
  const { isFreeUser, isPremiumUser, isEducationUser } = useUser();
  const isGated = (isFreeUser || isPremiumUser || isEducationUser) && !isReadonly;

  // if the widget is readonly and there is no image URL, it means it's an offer signature
  const isOfferSignature = imageURL && isReadonly;

  return (
    <Flex
      align='center'
      justify='center'
      position='relative'
      width={`calc(100% - .5rem)`}
      height={`calc(100% - .5rem)`}
      margin='.25rem'
      backgroundColor={backgroundColor}
      borderRadius={borderRadius}
      borderColor={borderColor}
      borderWidth={borderWidth}
      onMouseOver={setIsHover.on}
      onMouseLeave={setIsHover.off}
      onDoubleClick={() => !isOfferSignature && onDoubleClick()}
    >
      {imageURL ? (
        <Image
          position='absolute'
          padding={2}
          zIndex={1}
          src={imageURL}
          className='eSignature'
          data-created-at={submission?.completed_at}
          alt='eSignature'
          objectFit='contain'
          maxH='100%'
          maxW='100%'
        />
      ) : (
        <TransparentImg />
      )}
      {isHover && !isOfferSignature && <HoverElement isGated={isGated} borderRadius={borderRadius} />}
    </Flex>
  );
};

export const SignatureModal = ({ isOpen, allowToResubmit, onConfirm, onClose }: ModalProps) => {
  const { email } = useUser();
  const { slug } = useWidget<SignatureWidgetData>();
  const { t } = useTranslation('editor_modals', { useSuspense: false, keyPrefix: 'signatureModal' });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl' isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalCloseButton />
        </ModalHeader>
        <ModalBody py={0}>
          <Flex alignItems='center' justifyContent='center'>
            {!slug ? (
              <Spinner />
            ) : (
              <DocusealForm
                src={`https://docuseal.co/d/${slug}`}
                email={email}
                rememberSignature={true}
                withTitle={false}
                withDownloadButton={false}
                withSendCopyButton={false}
                withFieldNames={false}
                withFieldPlaceholder={false}
                allowToResubmit={allowToResubmit}
                sendCopyEmail={false}
                minimize={false}
                skipFields={false}
                onComplete={onConfirm}
                customCss={signaturePadStyle}
                i18n={{ form_has_been_completed: t('submissionCompleted') }}
              />
            )}
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Flex gap={2}>
            <Text color={'var(--vg-colors-font-300)'} fontSize={'12'}>
              {t('disclaimer')}
            </Text>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const HoverElement = ({ isGated, borderRadius }: HoverElementProps) => {
  const backgroundColor = isGated ? '#E6F1FDF2' : 'var(--vg-colors-hover-gray)';
  const imageSrc = isGated ? interactiveSignatureGatedHoverImg : interactiveSignatureHoverImg;
  const label = isGated ? 'Upgrade to add signature' : 'Signature';
  const color = isGated ? 'var(--vg-colors-upgrade-blue-500)' : 'font.300';

  const onClick = () => {
    isGated && Mixpanel.track(CLICK_ON_PAID_FEATURE, { Feature: UPGRADE.feature.Signature });
  };

  return (
    <Flex
      align='center'
      justify='center'
      direction='column'
      width='100%'
      height='100%'
      borderRadius={borderRadius}
      _hover={{ backgroundColor }}
      zIndex={2}
      overflow='hidden'
      userSelect='none'
      onClick={onClick}
    >
      <Image src={imageSrc} alt='eSignature icon' />
      <Flex align='center' justify='center' mt={2}>
        {isGated && <CrownIcon color='var(--vg-colors-upgrade-blue-500)' />}
        <Text fontSize={'xs'} color={color} whiteSpace='nowrap' overflow='hidden'>
          {label}
        </Text>
      </Flex>
    </Flex>
  );
};

export const ReadOnlySignatureWidget = () => {
  const { slug, updateWidget } = useWidget<SignatureWidgetData>();
  const [isModalOpen, setIsModalOpen] = useBoolean(false);
  // control the resubmission of the document
  const [canResubmit, setCanResubmit] = useBoolean(true);
  const token = useLoaderData() as string;
  const { enableEditorKeyboardShortcuts, disableEditorKeyboardShortcuts } = useEditor();

  const onDocumentSubmit = useCallback((data: DocuSealSubmitDocumentResponse) => {
    // get signature image URL from docuseal response
    const signatureImageUrl = data.values[0].value;

    updateWidget({ submissionId: data.submission_id, submission: data, imageURL: signatureImageUrl });

    // limit the user to resubmit the document after first submission
    setCanResubmit.off();

    // MP event
    Mixpanel.track(SIGNATURE_EDITED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openModal = useCallback(() => {
    setIsModalOpen.on();
    // disable keyboard shortcuts when the modal is open
    disableEditorKeyboardShortcuts();
    // MP event
    Mixpanel.track(SIGNATURE_MODAL_OPENED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen.off();
    // enable keyboard shortcuts again
    enableEditorKeyboardShortcuts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // at first, we will only create docuseal document when user opens the modal
  const handleOpen = useCallback(async () => {
    openModal();

    if (!slug) {
      const { slug } = await cloneDocuSealDocument(token);

      // update widget with docuseal slug to render it on modal
      updateWidget({ slug });
    }
  }, [openModal, slug, token, updateWidget]);

  return (
    <ReadOnlyWidgetBase>
      <InteractiveSignature onDoubleClick={handleOpen} isReadonly />
      <SignatureModal
        isOpen={isModalOpen}
        allowToResubmit={canResubmit}
        onClose={closeModal}
        onConfirm={onDocumentSubmit}
      />
      {/* Render alt image for adding the signature widget into the PDF file */}
    </ReadOnlyWidgetBase>
  );
};
